import type { AuthContext } from "./types";
import { ForbiddenError, UnauthorizedError } from "../lib/errors";

// ─── Capability check helpers ───────────────────────────────

/** Does the context have at least one of the given capabilities? */
export function hasCapability(ctx: AuthContext, ...capabilityIds: string[]): boolean {
  if (ctx.isPlatformAdmin) return true;
  return capabilityIds.some((id) => ctx.capabilities.has(id));
}

/** Does the context have ALL of the given capabilities? */
export function hasAllCapabilities(ctx: AuthContext, ...capabilityIds: string[]): boolean {
  if (ctx.isPlatformAdmin) return true;
  return capabilityIds.every((id) => ctx.capabilities.has(id));
}

/** Throws ForbiddenError if missing. Returns void on success. */
export function requireCapability(ctx: AuthContext, ...capabilityIds: string[]): void {
  if (!ctx.userId) throw new UnauthorizedError();
  if (!hasCapability(ctx, ...capabilityIds)) {
    throw new ForbiddenError(
      `Missing required capability: ${capabilityIds.join(" or ")}`,
    );
  }
}

/** Build an AuthContext from a session — typically called in the API route handler. */
export function buildAuthContext(input: {
  userId: string;
  organizationId: string | null;
  locationId?: string | null;
  capabilities: string[];
  isPlatformAdmin: boolean;
}): AuthContext {
  return {
    userId: input.userId,
    organizationId: input.organizationId,
    locationId: input.locationId ?? null,
    capabilities: new Set(input.capabilities),
    isPlatformAdmin: input.isPlatformAdmin,
  };
}
